import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wind, AlertTriangle } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [purchaseBlock, setPurchaseBlock] = useState(null); // { message, checkoutUrl }
  const [params] = useSearchParams();
  const stale = params.get("stale") === "1";
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result?.ok) { nav("/dashboard"); return; }
    if (result?.purchaseRequired) {
      setPurchaseBlock({ checkoutUrl: result.checkoutUrl || "" });
    }
  };

  return (
    <div className="min-h-screen shell-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center"><Wind size={20} className="text-gold" /></div>
          <div className="font-display font-bold text-navy text-xl">Breathe<span className="text-gold">·</span>Thrive</div>
        </Link>
        <div className="bg-warm-white border border-edge rounded-3xl p-8 shadow-[0_8px_30px_rgba(44,45,91,0.06)]">
          <h1 className="font-display text-2xl font-bold text-navy">Welcome back</h1>
          <p className="text-sm text-ink-mid mt-1">Continue your family's journey.</p>
          {stale && (
            <div data-testid="login-stale-banner" className="mt-5 flex items-start gap-2 text-sm text-mauve bg-mauve-pale border border-mauve/40 rounded-lg px-3 py-2.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>Your session expired for security. Sign in again to pick up where you left off — any typed answers may need to be re-entered.</div>
            </div>
          )}
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
            </div>
            {purchaseBlock ? (
              <div data-testid="login-purchase-required" className="text-sm bg-gold/10 border border-gold/40 rounded-xl px-4 py-3 space-y-3">
                <div className="font-display text-navy font-semibold">Purchase required</div>
                <div className="text-ink-mid">We couldn't find a Breathe to Thrive enrollment tied to this email. To sign in, please complete your purchase first.</div>
                <a
                  data-testid="login-purchase-cta"
                  href={purchaseBlock.checkoutUrl || "https://www.breathingintolifeco.com/breathe-to-thrive"}
                  className="inline-flex items-center bg-navy text-warm-white px-5 py-2.5 rounded-full font-semibold hover:-translate-y-0.5 transition-all"
                >
                  Complete your purchase
                </a>
                <div className="text-xs text-ink-soft">Already paid? Wait ~1 minute for the confirmation, then try signing in again.</div>
              </div>
            ) : error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <button data-testid="login-submit" disabled={loading} className="w-full bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all disabled:opacity-50">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="mt-4 text-sm text-ink-mid text-center">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-navy font-semibold underline underline-offset-4">Forgot your password?</Link>
          </p>
          <p className="mt-3 text-sm text-ink-mid text-center">
            No account yet? <Link to="/register" className="text-navy font-semibold underline underline-offset-4">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
